<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="
      max-container
      min-h-[750px] lg:min-h-[630px] xl:min-h-[550px] 2xl:min-h-[500px]
      grid grid-cols-1 flex-nowrap lg:grid-cols-3 lg:gap-x-16
      bg-transparent my-auto py-4 lg:py-8
    "
  >
    <div class="lg:hidden col-span-1 h-fit">
      <h2
        class="text-center max-w-sm md:max-w-xl text-pretty mx-auto after:hidden! px-8"
      >
        We transform the&nbsp;future of&nbsp;software through research&nbsp;in...
      </h2>
      <div class="h-1 w-16 bg-red-600 mx-auto mt-4"></div>
    </div>
    <div
      class="
        flex flex-col col-span-1 -my-30 lg:my-0
        relative
      "
    >
      <div
        class="
          h-96
          w-full
          absolute
          before:w-full before:h-32 before:bg-linear-to-b before:from-white before:to-transparent before:absolute before:left-0 before:top-0 before:z-1
          after:w-full after:h-32 after:bg-linear-to-t after:from-white after:to-transparent after:absolute after:left-0 after:bottom-0 after:z-1
          lg:border-gray-100 lg:border-solid lg:border-l-2 lg:rounded-[3%_47%_47%_3%/50%_0%_0%_50%]
          pointer-events-none
        "
      >
        <div
          id="rolodex-highlight"
          class="h-12 w-full lg:bg-linear-to-r from-red-300/5 to-transparent mix-blend-multiply lg:border-l-4 lg:border-red-600 top-1/2 -left-[3px] absolute translate-y-[-50%] z-1"
        ></div>
      </div>
      <div
        id="rolodex-list"
        ref="rolodexListRef"
        class="
          max-h-96 overflow-y-auto flex flex-col snap-y lg:snap-mandatory no-scrollbar overscroll-contain
        "
        @scroll.passive="onScrollOrResize"
      >
        <div class="min-h-48 snap-none">
          <div class="h-48"></div>
          <a
            v-for="(tp, index) in topics"
            :id="`rolodex-topic-${index}`"
            :key="index"
            :data-index="index"
            :href="tp.url"
            tabindex="0"
            class="
              flex flex-col justify-center w-full lg:w-[calc(100%-6px)]
              mx-0 lg:mx-0.5 px-4 sm:px-8 min-h-10 sm:min-h-12 text-sm text-center lg:text-left
              snap-center text-gray-600 no-underline hover:text-red-600 select-none
            "
            data-id="rolodex-link"
            @mousedown="topicMousedown"
            @click="topicClick"
            @keydown="topicKeydown"
            @focus="topicFocus"
          >
            {{ tp.title }}
          </a>
          <div class="h-48"></div>
        </div>
      </div>
    </div>
    <div class="lg:flex flex-col col-span-1 lg:col-span-2 lg:gap-y-4 relative z-2">
      <div class="hidden lg:block">
        <div
          id="rolodex-progress"
          class="relative h-1 mb-6 bg-gray-100 w-full"
        ></div>
        <div id="rolodex-content">
          <h2
            id="rolodex-title"
          >
            We transform the future of software through research in <a
              id="rolodex-link"
              href="#rolodex-link"
              tabindex="0"
            ></a>
          </h2>
          <span id="rolodex-description"></span>
          <ul id="rolodex-featured-list"></ul>
        </div>
      </div>
      <a
        href="/research-development#v-0"
        tabindex="0"
        class="cta-link ml-auto mr-auto lg:ml-0"
      >
        <span>Discover all research topics</span>
        <FaIcon
          name="arrow-right"
          icon-style="regular"
          icon-size="lg"
          icon-class="[--fa-display:flex] flex-col justify-center"
        />
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
interface FeaturedProject {
  title: string,
  url: string
}

interface Topic {
  title: string,
  description: string,
  url: string,
  featured_projects: FeaturedProject[]
}

interface Rolodex {
  uuid?: string,
  topics?: Topic[]
}

const props = withDefaults(defineProps<Rolodex>(), {
  uuid: '',
  topics: () => []
});

const rootElement = ref(null);
const rolodexListRef = ref<HTMLElement | null>(null);

onBeforeMount(() => {
  rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
  delete rootElement.value.dataset['json']
});

// --- Progress bar state ---
let progressInterval: ReturnType<typeof setInterval> | null = null;
let progressTimeout: ReturnType<typeof setTimeout> | null = null;
let currentIndex = ref(0);

const listElem = ref<HTMLElement>()
const highlight = computed(() => {
  return document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-highlight`) as HTMLElement;
})
const topicLinks = computed(() => {
  return Array.from(document.querySelectorAll(`[data-uuid="${props.uuid}"] [id^="rolodex-topic-"]`)) as HTMLElement[];
})

const lastOverlappedIndex = ref(-1)

// Apply bold/red highlight styling to the active topic, removing it from all others
const highlightTopic = (idx: number) => {
  lastOverlappedIndex.value = idx;
  topicLinks.value?.forEach((el, i) => {
    if (i === idx) {
      el.classList.add('font-bold', 'text-red-600');
    } else {
      el.classList.remove('font-bold', 'text-red-600');
    }
  });
}

// Guard flag: when true, scroll-driven topic detection is suppressed
// so programmatic scrollTo from click/keyboard isn't overridden.
let programmaticScroll = false;
let programmaticScrollTimer: ReturnType<typeof setTimeout> | null = null;

const setProgrammaticScroll = () => {
  programmaticScroll = true;
  if (programmaticScrollTimer) clearTimeout(programmaticScrollTimer);
  // Release after smooth scroll settles
  programmaticScrollTimer = setTimeout(() => {
    programmaticScroll = false;
  }, 600);
}

// --- Progress bar and content update logic ---
const showProgress = (index: number) => {
  currentIndex.value = index;
  highlightTopic(index);
  const p = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-progress`) as HTMLElement;
  if (!p) return;

  // Create animated progress bar element
  const pBar = document.createElement('div');
  pBar.id = 'rolodex-progress-bar';
  pBar.classList = 'absolute h-1 top-0 left-0 bg-red-600 w-full origin-left scale-x-0 transition-transform ease-linear duration-[10s]';

  const newLink = document.createElement('a');
  newLink.id = 'rolodex-link';
  newLink.href = props.topics[index]?.url;
  newLink.tabIndex = 0;
  newLink.textContent = props.topics[index]?.title;
  newLink.classList = 'opacity-0 transition-[opacity,top] duration-500 relative top-4';

  const newDesc = document.createElement('div');
  newDesc.id = 'rolodex-description';
  newDesc.textContent = props.topics[index]?.description;
  newDesc.classList = 'body-1 opacity-0 transition-[opacity,top] duration-500 relative top-4';

  const getProjectsForTopic = (index: number) => {
    const topics = props.topics;
    if (!topics || !topics[index]) return [];
    return topics[index].featured_projects || [];
  }

  // Fetch featured projects for the selected topic
  const projects = getProjectsForTopic(index);
  const newFeatured = document.createElement('ul');

  newFeatured.id = 'rolodex-featured-list';
  newFeatured.classList = 'body-2 opacity-0 transition-[opacity,top] duration-500 relative top-4 pl-8';

  if (projects.length) {
    const heading = document.createElement('h3');

    heading.id = 'rolodex-featured-heading';
    heading.textContent = 'Featured projects';
    heading.className = 'text-base font-bold mt-4 mb-2 text-gray-800';

    newDesc.appendChild(heading);

    // Populate the featured projects list with new items
    interface FeaturedProject {
      title: string;
      url: string;
    }

    projects.forEach((project: FeaturedProject) => {
      const listItem = document.createElement('li');
      listItem.className = [
        "before:content-['']",
        "before:block",
        "before:relative",
        "before:top-[15px]",
        "before:bg-red-600",
        "before:h-1",
        "before:w-1",
        "before:-left-2.5",
      ].join(' ');
      listItem.style.listStyleType = 'none';

      const link = document.createElement('a');
      link.href = project.url;
      link.tabIndex = 0;
      link.textContent = project.title;

      listItem.appendChild(link);
      newFeatured.appendChild(listItem);
    });
  }

  const title = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-title`) as HTMLElement;
  const link = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-link`) as HTMLAnchorElement;
  const content = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-content`) as HTMLElement;
  const desc = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-description`) as HTMLElement;
  const featured = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-featured-list`) as HTMLElement;

  if (title && link && content && desc && featured) {
    link?.classList.remove('opacity-100', 'top-0');
    link?.classList.add('-top-4');
    desc?.classList.remove('opacity-100', 'top-0');
    desc?.classList.add('-top-4');
    featured?.classList.remove('opacity-100', 'top-0');
    featured?.classList.add('-top-4');

    // Center the topic link inside the scrollable list, without scrolling the page.
    // Skip if a programmatic scroll from click/keyboard is already in progress,
    // since centerCurrentTopic already issued the scrollTo.
    const topicElem = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-topic-${index}`) as HTMLElement;

    if (topicElem && listElem.value && !programmaticScroll) {
      // Use getBoundingClientRect relative to the scroll container to avoid
      // offsetTop/offsetParent inconsistencies across browsers.
      const listRect = listElem.value.getBoundingClientRect();
      const topicRect = topicElem.getBoundingClientRect();
      const topicCenterInList = (topicRect.top + topicRect.height / 2) - listRect.top + listElem.value.scrollTop;
      const listVisibleCenter = listElem.value.clientHeight / 2;

      // Temporarily disable scroll-snap so Firefox's mandatory snapping
      // doesn't intercept the smooth scroll and snap to an adjacent item.
      listElem.value.style.scrollSnapType = 'none';

      listElem.value.scrollTo({
        top: topicCenterInList - listVisibleCenter,
        behavior: 'smooth',
      });

      setTimeout(() => {
        listElem.value.style.scrollSnapType = '';
      }, 600);
    }

    setTimeout(() => {
      title?.replaceChild(newLink, link);
      content?.replaceChild(newDesc, desc);
      content?.replaceChild(newFeatured, featured);
    }, 500);

    setTimeout(() => {
      newLink.classList.remove('top-4');
      newLink.classList.add('opacity-100', 'top-0');
      newDesc.classList.remove('top-4');
      newDesc.classList.add('opacity-100', 'top-0');
      newFeatured.classList.remove('top-4');
      newFeatured.classList.add('opacity-100', 'top-0');
    }, 550);
  }

  // Dynamically add the animated progress bar to the parent element
  if (!p.children) p?.appendChild(pBar);
  else p?.replaceChildren(pBar);

  // Start animation
  progressTimeout = setTimeout(() => {
    if (pBar) pBar.classList.add('scale-x-100');
  }, 1000);
}

const stopProgressInterval = () => {
  // Clear any previous interval/timeout
  if (progressInterval) clearInterval(progressInterval);
  if (progressTimeout) clearTimeout(progressTimeout);
  progressInterval = null;
  progressTimeout = null;
  return
}

// --- Progress bar auto-advance logic ---
const startProgressInterval = () => {
  stopProgressInterval();

  // Show current topic
  showProgress(currentIndex.value);

  // Start interval for next topics
  progressInterval = setInterval(() => {
    let nextIndex = currentIndex.value + 1;
    if (nextIndex >= props.topics.length) nextIndex = 0;
    showProgress(nextIndex);
    currentIndex.value = nextIndex;
  }, 11000); // 10s + 1ms timeout

  return
}

const topicMousedown = (e: MouseEvent) => {
  const el = (e.target as HTMLElement).closest('[data-index]') as HTMLElement;
  if (!el) return;
  const textAlign = window.getComputedStyle(el).getPropertyValue('text-align');

  // On mobile (centered text), allow default behavior for navigation
  if (textAlign === 'center') return;

  // On desktop, prevent the browser's native focus (which causes Firefox's
  // focus-scroll) and manually focus with preventScroll instead.
  e.preventDefault();
  el.focus({ preventScroll: true });
  setProgrammaticScroll();
}

const topicClick = (e: MouseEvent) => {
  const el = (e.target as HTMLElement).closest('[data-index]') as HTMLElement;
  if (!el) return;
  const textAlign = window.getComputedStyle(el).getPropertyValue('text-align');

  // On mobile (centered text), allow default navigation to topic URL
  if (textAlign === 'center') return;

  // On desktop, prevent navigation and scroll/activate the clicked topic.
  // Focus and preventScroll already handled by topicMousedown.
  e.preventDefault();
  const index = Number(el.dataset.index);
  setProgrammaticScroll();
  currentIndex.value = index;
  centerCurrentTopic(el, index);
  startProgressInterval();
}

const topicKeydown = (e: KeyboardEvent) => {
  let restart = 0
  if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
    e.preventDefault();
    if (currentIndex.value === topicLinks.value?.length - 1) {
      currentIndex.value = 0;
      restart = 1
    } else {
      currentIndex.value++;
      restart = 1
    }
  }
  if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
    if (currentIndex.value === 0) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        currentIndex.value = topicLinks.value?.length - 1;
        restart = 1
      } else {
        return // allow default tab behavior to move focus out of rolodex
      }
    } else {
      e.preventDefault();
      currentIndex.value--;
      restart =1
    }
  }
  if (e.key === 'ArrowRight') {
    e.preventDefault();
    // Focus the topic link in the right column when on desktop if it's visible
    const topicLink = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-link`) as HTMLElement;
    topicLink?.focus()
    return
  }
  if (restart === 0) return // no change in index, do nothing
  topicLinks.value[currentIndex.value]?.focus({ preventScroll: true });
  setProgrammaticScroll();
  startProgressInterval(); // resets interval and updates content/progress
}

const topicFocus = (e: FocusEvent) => {
  // Skip if this focus was triggered by a programmatic scroll (click or keyboard handler),
  // since those handlers already called centerCurrentTopic directly.
  if (programmaticScroll) return;
  const el = e.target as HTMLElement;
  const index = Number(el.dataset.index);
  setProgrammaticScroll();
  centerCurrentTopic(el, index);
  currentIndex.value = index;
}

const updateScales = () => {
  const highlightRect = highlight.value?.getBoundingClientRect();
  if (!highlightRect) return;
  const highlightCenterY = (highlightRect.top + highlightRect.bottom) / 2;

  topicLinks.value?.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.height === 0) return;
    const topicCenterY = (rect.top + rect.bottom) / 2;
    const dist = Math.abs(topicCenterY - highlightCenterY);
    const maxDist = listElem.value.clientHeight / 2;
    const t = Math.min(dist / maxDist, 1); // normalized [0,1]
    // Quadratic drop-off
    let scale = 1 - t * t * 0.05;
    scale = Math.max(0.9, Math.min(1, scale));
    el.style.transform = `scale(${scale})`;
  });
}

const centerCurrentTopic = (el: HTMLElement, idx: number) => {
  // Scroll the focused topic into the highlight area.
  // Use getBoundingClientRect relative to the scroll container to avoid
  // offsetTop/offsetParent inconsistencies across browsers.
  const listRect = listElem.value.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const elCenterInList = (elRect.top + elRect.height / 2) - listRect.top + listElem.value.scrollTop;
  const listVisibleCenter = listElem.value.clientHeight / 2;

  // Temporarily disable scroll-snap so Firefox's mandatory snapping
  // doesn't intercept the smooth scroll and snap to an adjacent item.
  listElem.value.style.scrollSnapType = 'none';

  listElem.value.scrollTo({
    top: elCenterInList - listVisibleCenter,
    behavior: 'smooth',
  });

  // Re-enable scroll-snap after the smooth scroll settles.
  // The timeout matches the programmaticScroll guard duration.
  setTimeout(() => {
    listElem.value.style.scrollSnapType = '';
  }, 600);

  // Update highlight styling immediately
  highlightTopic(idx);

  // Activate the topic (update content/progress)
  if (currentIndex.value !== idx) {
    currentIndex.value = idx;
    startProgressInterval(); // resets interval and updates content/progress
  }
}

// Debounce timer
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const getOverlapIndex = () => {
  const highlightRect = highlight.value?.getBoundingClientRect();
  let minDist = Infinity;
  let overlappedIdx = -1;

  topicLinks.value?.forEach((el, idx) => {
    const rect = el?.getBoundingClientRect();
    if (rect.height === 0) return;
    const topicCenterY = (rect.top + rect.bottom) / 2;
    const highlightCenterY = (highlightRect.top + highlightRect.bottom) / 2;
    const dist = Math.abs(topicCenterY - highlightCenterY);
    if (dist < minDist) {
      minDist = dist;
      overlappedIdx = idx;
    }
  });
  return overlappedIdx;
}

// This function is called after debounce delay
const updateOnScrollStop = () => {
  // Skip scroll-driven topic detection during programmatic scrolls
  // (from click or keyboard) to prevent overriding the intended target.
  if (programmaticScroll) return;
  const idx = getOverlapIndex();
  if (idx !== lastOverlappedIndex.value && idx !== -1) {
    highlightTopic(idx);

    // --- Interrupt progress and update to this topic ---
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (typeof currentIndex.value !== 'undefined' && currentIndex.value !== idx) {
        currentIndex.value = idx;
        startProgressInterval(); // resets interval and updates content/progress
      }
    }, 250);
  }
}

// Debounced scroll/resize handler
const onScrollOrResize = () => {
  updateOnScrollStop();
  updateScales();
}

const initialize = () => {
  listElem.value = document.querySelector(`[data-uuid="${props.uuid}"] #rolodex-list`);
  startProgressInterval();
  window.addEventListener('resize', onScrollOrResize);

  // Add wheel event listener for scroll chaining
  if (rolodexListRef.value) {
    rolodexListRef.value.addEventListener('wheel', (e: WheelEvent) => {
      const target = e.currentTarget as HTMLElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      const delta = e.deltaY;

      const isAtTop = scrollTop <= 1;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      const isScrollingUp = delta < 0;
      const isScrollingDown = delta > 0;

      // If at top and scrolling up, scroll the page instead
      if (isAtTop && isScrollingUp) {
        e.preventDefault();
        window.scrollBy({
          top: delta,
          behavior: 'instant'
        });
        return;
      }

      // If at bottom and scrolling down, scroll the page instead
      if (isAtBottom && isScrollingDown) {
        e.preventDefault();
        window.scrollBy({
          top: delta,
          behavior: 'instant'
        });
        return;
      }

      // Otherwise allow normal rolodex scrolling (don't prevent)
    }, { passive: false });
  }

  setTimeout(() => {
    updateOnScrollStop();
    updateScales();
  }, 100);
}

onMounted(() => { initialize() });
</script>
